import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, ChevronDown, Package, ShieldCheck, LogOut } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/auth";
import { useCartStore } from "../store/cart";
import { useGuestCartStore } from "../store/guestCart";
import { useCurrencyStore, SUPPORTED_CURRENCIES } from "../store/currency";
import { useTenant } from "../lib/tenant";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthStore();
  const authCart = useCartStore();
  const guestCart = useGuestCartStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currency, setCurrency, enabledCurrencies, siteMode } = useCurrencyStore();
  const tenant = useTenant();

  const filteredCurrencies = SUPPORTED_CURRENCIES.filter(c => enabledCurrencies.includes(c.code));

  const isGuest = !user;
  const items = isGuest ? guestCart.items : authCart.items;
  const toggleOpen = isGuest ? guestCart.toggleOpen : authCart.toggleOpen;
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);

  const navLinks = [
    { to: "/shop", label: t("nav.shop") },
    { to: "/about", label: t("nav.about") },
  ];

  function toggleLang() {
    i18n.changeLanguage(i18n.language.startsWith("ja") ? "en" : "ja");
  }

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (section?: string) => {
    setUserDropdownOpen(false);
    if (section) {
      navigate(`/account?section=${section}`);
    } else {
      navigate("/account");
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
      {siteMode === "read_only" && (
        <div className="bg-brand-600 text-white text-[10px] sm:text-xs font-bold py-1.5 px-4 text-center tracking-wider uppercase" style={{ backgroundColor: `rgb(${tenant.themeColor.replace("rgb(", "").replace(")", "")})` }}>
          Site is in View-Only mode. Orders are currently disabled.
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src={`/branding/${tenant.id}/logo.${tenant.id === 'chlorine.art' ? 'svg' : 'png'}`} 
              alt={tenant.name} 
              style={{ height: "var(--logo-height)" }}
              className="w-auto object-contain"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.src.endsWith('.svg')) {
                  // Try png if svg fails
                  img.src = `/branding/${tenant.id}/logo.png`;
                  return;
                }
                // Fallback to text if all images missing
                img.style.display = 'none';
                const parent = img.parentElement;
                if (parent && !parent.querySelector('.logo-text')) {
                  const span = document.createElement('span');
                  span.className = 'logo-text font-bold text-xl tracking-tight';
                  span.innerText = tenant.name;
                  parent.appendChild(span);
                }
              }}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "" : "text-gray-600 hover:opacity-70"
                  }`
                }
                style={({ isActive }) => isActive ? { color: 'rgb(var(--brand-color))' } : {}}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Currency selector */}
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as any)}
              className="hidden md:block rounded-full border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:border-brand-300 hover:text-brand-600 transition-colors bg-white cursor-pointer"
              title="Select currency"
            >
              {filteredCurrencies.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>

            {/* Language switcher */}
            <button
              onClick={toggleLang}
              className="hidden md:flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:border-brand-300 hover:text-brand-600 transition-colors"
              title="Switch language"
            >
              {i18n.language.startsWith("ja") ? "EN" : "日本語"}
            </button>

            {/* Cart */}
            <button
              onClick={toggleOpen}
              className="relative p-2 text-gray-600 hover:text-brand-600 transition-colors"
              aria-label="Open cart"
            >
              <ShoppingCart size={22} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-600 text-[10px] font-bold text-white">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </button>

            {/* Auth */}
            {user ? (
              <div className="hidden md:block relative" ref={userDropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-brand-600 transition-colors py-2"
                >
                  <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100">
                    <User size={16} />
                  </div>
                  <span className="max-w-[100px] truncate">{user.name.split(" ")[0]}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${userDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {userDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-56 origin-top-right bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 animate-in fade-in zoom-in-95 duration-200">
                    <div className="px-4 py-3 border-b border-gray-50 mb-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t("nav.account")}</p>
                      <p className="text-sm font-bold text-gray-900 truncate mt-0.5">{user.name}</p>
                    </div>

                    <button
                      onClick={() => handleNavClick("profile")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <User size={16} className="text-gray-400" />
                      Profile
                    </button>

                    <button
                      onClick={() => handleNavClick("orders")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <Package size={16} className="text-gray-400" />
                      Orders
                    </button>

                    <button
                      onClick={() => handleNavClick("privacy")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <ShieldCheck size={16} className="text-gray-400" />
                      Data Privacy
                    </button>

                    <div className="my-1 border-t border-gray-50" />

                    <button
                      onClick={() => { logout(); setUserDropdownOpen(false); navigate("/"); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                    >
                      <LogOut size={16} />
                      {t("nav.signOut")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-brand-600 transition-colors">
                  {t("nav.signIn")}
                </Link>
                <Link to="/register" className="btn-primary !py-1.5 !px-4 !text-xs">
                  {t("nav.join")}
                </Link>
              </div>
            )}

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 pb-4 pt-2 space-y-2">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `block py-2 text-sm font-medium ${isActive ? "text-brand-600" : "text-gray-700"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            onClick={toggleLang}
            className="block py-2 text-sm font-medium text-gray-500"
          >
            {i18n.language.startsWith("ja") ? "English" : "日本語"}
          </button>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as any)}
            className="block py-2 text-sm font-medium text-gray-500 bg-transparent border-none cursor-pointer"
          >
            {filteredCurrencies.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          {user ? (
            <>
              <Link to="/account" onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-medium text-gray-700">
                {t("nav.account")}
              </Link>
              <button onClick={() => { logout(); setMenuOpen(false); }} className="block py-2 text-sm text-gray-500">
                {t("nav.signOut")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-medium text-gray-700">
                {t("nav.signIn")}
              </Link>
              <Link to="/register" onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-medium text-brand-600">
                {t("nav.join")}
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
